import { createClient } from '@launchdarkly/react-sdk';
import type {
  LDContext,
  LDReactClient,
  LDReactClientOptions,
} from '@launchdarkly/react-sdk';

/**
 * An {@link LDReactClient} augmented with local, session-only flag overrides.
 * Behaves identically to the underlying client except that overridden flags
 * return the override value from `variation`, the typed `*Variation` methods,
 * and `allFlags`.
 */
export type OverridableLDClient = LDReactClient & {
  /** Override a flag's value locally and notify subscribers. */
  setOverride: (key: string, value: unknown) => void;
  /** Remove a single override, reverting the flag to its real value. */
  clearOverride: (key: string) => void;
  /** Remove all local overrides. */
  clearAllOverrides: () => void;
  /** The current local overrides as a plain object. */
  getOverrides: () => Record<string, unknown>;
};

const VARIATION_METHODS = new Set([
  'variation',
  'boolVariation',
  'stringVariation',
  'numberVariation',
  'jsonVariation',
]);

/**
 * Wraps an LD client so flag values can be overridden locally, session-only —
 * for simulating flags on/off without touching the LaunchDarkly dashboard.
 *
 * Overrides take precedence in `variation`, the typed `*Variation` methods, and
 * `allFlags`; all other methods delegate to the real client. Setting or clearing
 * an override fires the client's `change` listeners so subscribed hooks
 * re-render with the new value.
 *
 * @param client - The real LD client to wrap.
 * @returns The client augmented with the override controls in
 *   {@link OverridableLDClient}.
 */
export const wrapWithOverrides = (
  client: LDReactClient,
): OverridableLDClient => {
  const overrides = new Map<string, unknown>();
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach(fn => fn());

  return new Proxy(client, {
    get(target, prop, receiver) {
      switch (prop) {
        case 'setOverride':
          return (key: string, value: unknown) => {
            overrides.set(key, value);
            notify();
          };
        case 'clearOverride':
          return (key: string) => {
            overrides.delete(key);
            notify();
          };
        case 'clearAllOverrides':
          return () => {
            overrides.clear();
            notify();
          };
        case 'getOverrides':
          return () => Object.fromEntries(overrides);
        case 'allFlags':
          return () => ({
            ...target.allFlags(),
            ...Object.fromEntries(overrides),
          });
        case 'on':
          return (event: string, cb: () => void) => {
            if (event === 'change') listeners.add(cb);
            return (target.on as (e: string, c: () => void) => void)(event, cb);
          };
        case 'off':
          return (event: string, cb: () => void) => {
            listeners.delete(cb);
            return (target.off as (e: string, c: () => void) => void)(
              event,
              cb,
            );
          };
        default: {
          if (typeof prop === 'string' && VARIATION_METHODS.has(prop)) {
            return (key: string, fallback: unknown) =>
              overrides.has(key)
                ? overrides.get(key)
                : (
                    target as unknown as Record<
                      string,
                      (k: string, f: unknown) => unknown
                    >
                  )[prop](key, fallback);
          }
          const value = Reflect.get(target, prop, receiver);
          return typeof value === 'function' ? value.bind(target) : value;
        }
      }
    },
  }) as unknown as OverridableLDClient;
};

/**
 * Creates an LD client via {@link createClient} and wraps it with local
 * override support. Convenience for the common case; call
 * {@link wrapWithOverrides} directly to wrap an existing client.
 *
 * @param clientSideId - LaunchDarkly client-side ID.
 * @param context - Initial LD context.
 * @param options - Optional LD client options.
 */
export const createOverridableLDClient = (
  clientSideId: string,
  context: LDContext,
  options?: LDReactClientOptions,
): OverridableLDClient =>
  wrapWithOverrides(createClient(clientSideId, context, options));
